import {
  OptroImportItemStatus,
  OptroImportJob,
} from "../models/audit/optroSync";

// Shared by the controls and work steps Optro imports: which of an import's
// items still need a placeholder row in their list, and which loaded rows to
// hold back while their item is unfinished.

const IN_FLIGHT: ReadonlySet<OptroImportItemStatus> = new Set([
  "queued",
  "processing",
]);
const LANDED: ReadonlySet<OptroImportItemStatus> = new Set([
  "complete",
  "no_workpaper",
]);

interface ImportItemAccessors<TItem> {
  // The ref the job's status items carry (`controlExternalRef`).
  statusRefOf: (item: TItem) => string;
  // Pairs an import item with the row it becomes.
  itemKeyOf: (item: TItem) => string;
}

// Keys of the items that finished with a row to show.
export const landedItemKeys = <TItem>(
  job: OptroImportJob | null,
  startItems: TItem[],
  { statusRefOf, itemKeyOf }: ImportItemAccessors<TItem>,
): ReadonlySet<string> => {
  const keys = new Set<string>();
  if (!job) return keys;
  const statusByRef = new Map(
    job.items.map((item) => [item.controlExternalRef, item.status]),
  );
  for (const item of startItems) {
    const status = statusByRef.get(statusRefOf(item));
    if (status && LANDED.has(status)) keys.add(itemKeyOf(item));
  }
  return keys;
};

interface SplitImportRowsArgs<TItem, TRow> extends ImportItemAccessors<TItem> {
  job: OptroImportJob;
  // The items the import was started with, in selection order.
  startItems: TItem[];
  rows: TRow[];
  // Landed items whose reload hasn't come back yet — see
  // useImportPlaceholders.
  awaitingReload: ReadonlySet<string>;
  // null for rows no import produced.
  rowKeyOf: (row: TRow) => string | null;
}

// An item is unfinished while it is in flight, or landed but still awaiting
// the reload that fetches its final row. Each unfinished item gets a
// placeholder, and any row already loaded for it is held back: the backend
// writes a row before it finishes configuring it, so a reload mid-import can
// have cached a half-made one.
export const splitImportRows = <TItem, TRow>({
  job,
  startItems,
  rows,
  awaitingReload,
  statusRefOf,
  itemKeyOf,
  rowKeyOf,
}: SplitImportRowsArgs<TItem, TRow>) => {
  const statusByRef = new Map(
    job.items.map((item) => [item.controlExternalRef, item.status]),
  );
  const importing: TItem[] = [];
  const unfinishedKeys = new Set<string>();
  for (const item of startItems) {
    // The optimistic job lists every selected item as queued until the first
    // poll replaces it with the server's, which only has items that were
    // actually started — one missing from it failed to start, so it gets no
    // placeholder (the start toast already reported it).
    const status = statusByRef.get(statusRefOf(item));
    if (status === undefined) continue;
    const key = itemKeyOf(item);
    if (
      IN_FLIGHT.has(status) ||
      (LANDED.has(status) && awaitingReload.has(key))
    ) {
      unfinishedKeys.add(key);
      importing.push(item);
    }
  }

  const visibleRows = unfinishedKeys.size
    ? rows.filter((row) => {
        const key = rowKeyOf(row);
        return key === null || !unfinishedKeys.has(key);
      })
    : rows;

  return { visibleRows, importing };
};
